import { Component, computed, effect, inject, input, output, signal } from "@angular/core";
import { Router } from "@angular/router";
import { FontAwesomeModule } from "@fortawesome/angular-fontawesome";
import { faChartSimple } from "@fortawesome/free-solid-svg-icons";
import { TranslatePipe } from "@ngx-translate/core";

import { PercentageValuePipe, TranslateTokenPipe } from "../../../../core/pipes";
import { AdaaHelper } from "../../../../core/utils";
import { Constants } from "../../../constants/constants";
import type { GovEnablerPillarModelType } from "../../../models";
import { FilesApiService } from "../../../services";
import { CountUpComponent } from "../../count-up";

@Component({
  selector: "adaa-gov-enabler-perf-card",
  templateUrl: "./gov-enabler-perf-card.html",
  styleUrl: "./gov-enabler-perf-card.scss",
  imports: [TranslateTokenPipe, PercentageValuePipe, TranslatePipe, CountUpComponent, FontAwesomeModule],
})
export class GovEnablerPerfCardComponent {
  readonly router = inject(Router);
  private readonly _fileService = inject(FilesApiService);

  data = input.required<GovEnablerPillarModelType>();
  graphClicked = output<GovEnablerPillarModelType>();

  logo = signal<string | undefined>(undefined);

  color = computed(() => this._evaluationColor(this.data().performance, this.data().performanceColor));
  progress = computed(() => this._progress(this.data().performance));
  initials = computed(() => this._initials(AdaaHelper.getItemValueByToken(this.data(), "name")));

  readonly defaultColor = Constants.COLOR_CODES[Constants.CONSTANT_COLORS.EVALUATION_GRAY];
  readonly graphIcon = faChartSimple;

  constructor() {
    effect(() => {
      this._downloadFile();
    });
  }

  public goToEkpis() {
    this.router.navigateByUrl(`/console/kpi/ekpi?pillar_id=${this.data().id}`);
  }

  public clickGraphIcon(event: Event) {
    event.stopPropagation();
    this.graphClicked.emit(this.data());
  }

  private _evaluationColor(value: number | undefined, colorCode: number | undefined) {
    if (!AdaaHelper.isDefined(value) || !AdaaHelper.isDefined(colorCode)) return this.defaultColor;
    return Constants.COLOR_CODES[colorCode];
  }

  private _progress(value: number | undefined) {
    if (!AdaaHelper.isDefined(value)) return 0;
    return Math.max(0, Math.min(100, value));
  }

  private _initials(name: string) {
    if (!AdaaHelper.isDefinedAndNotEmpty(name)) return "";

    return name
      .split(" ")
      .filter(Boolean)
      .slice(0, 2)
      .map((word) => word.charAt(0).toUpperCase())
      .join("");
  }

  private _downloadFile() {
    if (!this.data()?.logo) return;

    this._fileService.download(this.data().logo).subscribe({
      next: (blob) => {
        const dataUrl = window.URL.createObjectURL(blob);
        this.logo.update(() => dataUrl);
      },
    });
  }
}
